import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { alpha, type Theme } from '@mui/material/styles'
import { formatPhone } from '@jupi/shared'
import { ApiError, ConfirmDialog, IconButton, RemoteContent, Title, useSnackbar } from '@jupi/ui'
import {
  useFirePatrolCandidates,
  useFirePatrolCrew,
  useRemoveCrewMember,
  useSaveCrewMember,
  type FirePatrolCandidate,
  type FirePatrolCrewMember,
} from '../features/firePatrol/api'

// The translucent band behind an observer's two rows.
const tint = (theme: Theme) => alpha(theme.palette.info.main, 0.1)

// The Miehistölista: who is cleared to fly (PIC), observe (MC) and dispatch.
// Kept by, and only shown to, the dispatchers.
export default function FirePatrolCrewPage() {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { data: crew = [], isLoading, error } = useFirePatrolCrew()
  const { data: candidates = [] } = useFirePatrolCandidates()
  const save = useSaveCrewMember()
  const remove = useRemoveCrewMember()
  const [removing, setRemoving] = useState<FirePatrolCrewMember | null>(null)

  const fail = (err: unknown) =>
    showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })

  const toggle = (member: FirePatrolCrewMember, key: 'canPic' | 'canMc' | 'canDispatch', value: boolean) =>
    save
      .mutateAsync({
        userId: member.userId,
        canPic: member.canPic,
        canMc: member.canMc,
        canDispatch: member.canDispatch,
        [key]: value,
      })
      .catch(fail)

  const add = (candidate: FirePatrolCandidate | null) => {
    if (!candidate) return
    save.mutateAsync({ userId: candidate.userId, canPic: false, canMc: false, canDispatch: false }).catch(fail)
  }

  return (
    <Box>
      <Title label={t('firePatrol.crew.title')} />
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('firePatrol.crew.visibleToDispatchers')}
        </Typography>
        <Autocomplete
          options={candidates}
          value={null}
          onChange={(_, value) => add(value)}
          getOptionLabel={(option) => option.displayName}
          noOptionsText={t('firePatrol.crew.nobodyToAdd')}
          sx={{ maxWidth: 420 }}
          renderInput={(params) => (
            <TextField {...params} label={t('firePatrol.crew.add')} helperText={t('firePatrol.crew.addHint')} />
          )}
        />
        <RemoteContent isLoading={isLoading} error={error}>
          {crew.length === 0 && !isLoading ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {t('firePatrol.crew.none')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('firePatrol.crew.name')}</TableCell>
                    <TableCell align="center">{t('firePatrol.crew.pic')}</TableCell>
                    <TableCell align="center">{t('firePatrol.crew.mc')}</TableCell>
                    <TableCell align="center">{t('firePatrol.crew.dispatch')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crew.map((m) => {
                    const observer = m.membershipType === 'observer'
                    return (
                      <Fragment key={m.userId}>
                        <TableRow hover sx={observer ? { bgcolor: tint, '& > td': { borderBottom: 0 } } : undefined}>
                          <TableCell>
                            <div>{m.lastName && m.firstName ? `${m.lastName} ${m.firstName}` : m.displayName}</div>
                            {m.phone ? (
                              <Link href={`tel:${m.phone}`} variant="body2" underline="hover" sx={{ whiteSpace: 'nowrap' }}>
                                {formatPhone(m.phone)}
                              </Link>
                            ) : null}
                          </TableCell>
                          {(['canPic', 'canMc', 'canDispatch'] as const).map((key) => (
                            <TableCell key={key} align="center" padding="checkbox">
                              <Checkbox
                                checked={m[key]}
                                onChange={(e) => toggle(m, key, e.target.checked)}
                                slotProps={{ input: { 'aria-label': `${m.displayName} ${t(`firePatrol.crew.${key === 'canPic' ? 'pic' : key === 'canMc' ? 'mc' : 'dispatch'}`)}` } }}
                              />
                            </TableCell>
                          ))}
                          <TableCell align="right" padding="checkbox">
                            <IconButton aria-label={t('firePatrol.remove')} onClick={() => setRemoving(m)}>
                              <Icon icon="mdi:close" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {observer ? (
                          <TableRow sx={{ bgcolor: tint }}>
                            <TableCell colSpan={5} sx={{ py: 0.25, textAlign: 'center' }}>
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', letterSpacing: '0.2em', textTransform: 'uppercase' }}
                              >
                                {t('firePatrol.crew.observer')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </RemoteContent>
      </Stack>
      <ConfirmDialog
        open={removing !== null}
        title={t('firePatrol.remove')}
        message={t('firePatrol.crew.removeConfirm')}
        confirmLabel={t('firePatrol.remove')}
        cancelLabel={t('firePatrol.close')}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          const member = removing
          setRemoving(null)
          if (member) remove.mutateAsync(member.userId).catch(fail)
        }}
      />
    </Box>
  )
}

